import { useState } from "react";
import { Axes, ChartSvg, LinearScale, M, NiceTicks, SERIES, SURFACE_STROKE, Tooltip, W, H, type TooltipState } from "./ChartKit";
import type { GroupData } from "./playgroundData";

type Series = (typeof SERIES)[number];

function GroupBox({ group, cx, boxW, y, series }: { group: GroupData; cx: number; boxW: number; y: (v: number) => number; series: Series }) {
  const { min, q1, median, q3, max } = group.summary;
  return (
    <g>
      <line x1={cx} x2={cx} y1={y(min)} y2={y(q1)} strokeWidth={2} className={series.stroke} />
      <line x1={cx} x2={cx} y1={y(q3)} y2={y(max)} strokeWidth={2} className={series.stroke} />
      <rect x={cx - boxW / 2} y={y(q3)} width={boxW} height={y(q1) - y(q3)} rx={4} strokeWidth={2} className={`${series.fill} ${series.stroke} [fill-opacity:0.16]`} />
      <line x1={cx - boxW / 2} x2={cx + boxW / 2} y1={y(median)} y2={y(median)} strokeWidth={3} className={series.stroke} />
      {group.values.map((v, i) => (
        <circle key={i} cx={cx + boxW / 2 + 10 + ((i * 37) % 19)} cy={y(v)} r={3} strokeWidth={1} className={`${series.fill} ${SURFACE_STROKE} [fill-opacity:0.6]`} />
      ))}
    </g>
  );
}

function SummaryLines(name: string, g: GroupData): string[] {
  const f = (v: number) => v.toFixed(1);
  return [name, `Median ${f(g.summary.median)} · Mean ${f(g.mean)}`, `IQR ${f(g.summary.q1)}–${f(g.summary.q3)}`, `Range ${f(g.summary.min)}–${f(g.summary.max)}`];
}

export function CompareChart({ groups }: { groups: GroupData[] }) {
  const [tip, setTip] = useState<TooltipState | null>(null);
  const all = groups.flatMap((g) => g.values);
  const lo = Math.floor(Math.min(...all) / 10) * 10, hi = Math.ceil(Math.max(...all) / 10) * 10;
  const y = LinearScale([lo, hi], [H - M.bottom, M.top]);
  const band = (W - M.left - M.right) / groups.length;
  const boxW = Math.min(72, band * 0.4);
  return (
    <div className="relative">
      <ChartSvg label={`Box plots comparing ${groups.length} groups`} onLeave={() => setTip(null)}>
        <Axes xTicks={[]} yTicks={NiceTicks(lo, hi)} x={(v) => v} y={y} xLabel="" yLabel="Score" />
        {groups.map((g, i) => {
          const cx = M.left + band * (i + 0.4);
          return (
            <g key={SERIES[i].name}>
              <GroupBox group={g} cx={cx} boxW={boxW} y={y} series={SERIES[i]} />
              <text x={cx} y={H - M.bottom + 16} textAnchor="middle" className="fill-[#0A0A0F] dark:fill-white text-[11px] font-medium">{SERIES[i].name}</text>
              <rect x={M.left + band * i} y={M.top} width={band} height={H - M.top - M.bottom} fill="transparent"
                onMouseEnter={() => setTip({ x: cx + boxW / 2, y: y(g.summary.median), lines: SummaryLines(SERIES[i].name, g) })} />
            </g>
          );
        })}
      </ChartSvg>
      <Tooltip tip={tip} />
    </div>
  );
}
